/**
 * Keep SaaS sessions alive after the 30-minute access JWT expires.
 * Access tokens stay short-lived; a refresh token (8h) mints a new access token
 * at POST /api/v1/auth/refresh without sending the expired access JWT.
 */

const ACCESS_SKEW_MS = 2 * 60 * 1000;
const MIN_REFRESH_WAIT_MS = 15 * 1000;

let refreshInFlight = null;
let keepAliveTimer = null;
let keepAliveCleanup = null;
let lastHydratedUrlToken = null;

function trimBase(apiBaseUrl) {
  return String(apiBaseUrl || '').replace(/\/+$/, '');
}

export function resolveSaasApiBaseUrl(explicit) {
  if (explicit) return trimBase(explicit);
  if (typeof window === 'undefined' || !window.location) return '/api/v1';
  const { hostname, protocol, port } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1';
  if (local && (port === '3000' || port === '3001')) {
    return `${protocol}//${hostname}:8001/api/v1`;
  }
  return '/api/v1';
}

export function getAccessToken() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('token') || localStorage.getItem('auth_token') || null;
}

export function getRefreshToken() {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('refresh_token') || null;
}

export function persistSessionTokens({ accessToken, refreshToken } = {}) {
  if (typeof localStorage === 'undefined') return;
  if (accessToken) {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('auth_token', accessToken);
  }
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
}

export function persistAuthResponse(data) {
  const accessToken = data?.access_token || data?.token || null;
  const refreshToken = data?.refresh_token || data?.refreshToken || null;
  persistSessionTokens({ accessToken, refreshToken });
  return accessToken;
}

export function clearSessionTokens() {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem('token');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
}

/** Full client sign-out: stop refresh timers and wipe session + org profile from storage. */
export function clearAuthSession() {
  stopSessionKeepAlive();
  clearSessionTokens();
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem('user');
  localStorage.removeItem('selectedOrganizationId');
  localStorage.removeItem('organizationId');
  localStorage.removeItem('organization_id');
  localStorage.removeItem('selected_organization_id');
  localStorage.removeItem('organizationName');
  if (typeof window !== 'undefined' && window.queryClient) {
    window.queryClient.clear();
  }
}

export function hydrateAuthFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const fromUrl = new URLSearchParams(window.location.search);
    const urlToken = (fromUrl.get('auth_token') || fromUrl.get('token') || '').trim();
    const urlRefresh = (fromUrl.get('refresh_token') || '').trim();
    if (urlToken && urlToken !== lastHydratedUrlToken) {
      lastHydratedUrlToken = urlToken;
      persistSessionTokens({
        accessToken: urlToken,
        refreshToken: urlRefresh || undefined,
      });
    } else if (urlRefresh && !getRefreshToken()) {
      persistSessionTokens({ refreshToken: urlRefresh });
    }
  } catch {
    /* ignore */
  }
}

export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getAccessExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (!exp || typeof exp !== 'number') return null;
  return exp * 1000;
}

export function isAccessTokenExpiringSoon(token, skewMs = ACCESS_SKEW_MS) {
  if (!token) return true;
  const expMs = getAccessExpiryMs(token);
  if (!expMs) return true;
  return expMs - Date.now() <= skewMs;
}

export async function refreshSession({ apiBaseUrl } = {}) {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    const base = resolveSaasApiBaseUrl(apiBaseUrl);
    const response = await fetch(`${base}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refresh_token: refreshToken,
        refreshToken,
      }),
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        clearSessionTokens();
      }
      return null;
    }
    const data = await response.json().catch(() => ({}));
    const access = persistAuthResponse(data);
    return access || null;
  })()
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

export async function ensureValidAccessToken({ apiBaseUrl } = {}) {
  hydrateAuthFromUrl();
  const token = getAccessToken();
  if (token && !isAccessTokenExpiringSoon(token)) return token;
  if (!getRefreshToken()) return token || null;
  const refreshed = await refreshSession({ apiBaseUrl });
  return refreshed || getAccessToken();
}

export async function fetchWithFreshAccess(apiBaseUrl, path, options = {}) {
  const { _authRetry, headers: extraHeaders, ...fetchOptions } = options;
  const base = resolveSaasApiBaseUrl(apiBaseUrl);
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  await ensureValidAccessToken({ apiBaseUrl: base });
  const token = getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extraHeaders || {}),
  };
  const response = await fetch(url, { ...fetchOptions, headers });
  if (response.status !== 401 || _authRetry) return response;
  const refreshed = await refreshSession({ apiBaseUrl: base });
  if (!refreshed) return response;
  return fetch(url, {
    ...fetchOptions,
    headers: {
      ...headers,
      Authorization: `Bearer ${refreshed}`,
    },
  });
}

export function stopSessionKeepAlive() {
  if (keepAliveTimer) {
    clearTimeout(keepAliveTimer);
    keepAliveTimer = null;
  }
  if (typeof keepAliveCleanup === 'function') {
    keepAliveCleanup();
    keepAliveCleanup = null;
  }
}

export function startSessionKeepAlive({ getApiBaseUrl, onRefreshFailed } = {}) {
  stopSessionKeepAlive();
  if (typeof window === 'undefined') return () => {};

  const resolveBase = () =>
    resolveSaasApiBaseUrl(typeof getApiBaseUrl === 'function' ? getApiBaseUrl() : getApiBaseUrl);

  const tick = async () => {
    const apiBaseUrl = resolveBase();
    const token = await ensureValidAccessToken({ apiBaseUrl });
    if (!token && !getRefreshToken()) {
      onRefreshFailed?.();
      return;
    }
    scheduleNext();
  };

  const scheduleNext = () => {
    if (keepAliveTimer) clearTimeout(keepAliveTimer);
    const expMs = getAccessExpiryMs(getAccessToken());
    const delay = expMs
      ? Math.max(MIN_REFRESH_WAIT_MS, expMs - Date.now() - ACCESS_SKEW_MS)
      : 60 * 1000;
    keepAliveTimer = setTimeout(() => {
      void tick();
    }, delay);
  };

  const onFocus = () => {
    if (document.visibilityState && document.visibilityState !== 'visible') return;
    void tick();
  };

  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onFocus);
  scheduleNext();

  keepAliveCleanup = () => {
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };

  return () => stopSessionKeepAlive();
}

export function attachAxiosAccessRefresh(client, { getApiBaseUrl } = {}) {
  if (!client?.interceptors) return client;

  client.interceptors.request.use(async (config) => {
    const apiBaseUrl = resolveSaasApiBaseUrl(
      typeof getApiBaseUrl === 'function' ? getApiBaseUrl() : getApiBaseUrl
    );
    await ensureValidAccessToken({ apiBaseUrl });
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return client;
}
